import guestClient from './guestClient';
import { apiGet, apiPost } from '../services/apiClient';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const buildQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') qs.set(key, value);
  });
  const str = qs.toString();
  return str ? `?${str}` : '';
};

export const listGuestInvoices = async (params = {}) => {
  const res = await guestClient.get(`/api/guest/invoices${buildQuery(params)}`);
  return res.data;
};

export const ensureGuestInvoice = async (bookingId) => {
  const res = await guestClient.post(`/api/guest/invoices/ensure/${encodeURIComponent(bookingId)}`);
  return res.data?.invoice || res.data;
};

export const listHostInvoices = (params = {}, token) => {
  return apiGet(`/api/host/invoices${buildQuery(params)}`, token);
};

export const listAdminInvoices = (params = {}, token) => {
  return apiGet(`/api/admin/invoices${buildQuery(params)}`, token);
};

export const getInvoice = (invoiceId, token, { asGuest = false } = {}) => {
  if (asGuest) {
    return guestClient.get(`/api/invoices/${invoiceId}`).then((res) => res.data);
  }
  return apiGet(`/api/invoices/${invoiceId}`, token);
};

export const generateAdminInvoices = (bookingId, token) => {
  return apiPost(`/api/admin/invoices/generate/${bookingId}`, {}, token);
};

export const getInvoicePdfUrl = (invoiceId) => `${API_BASE}/api/invoices/${invoiceId}/pdf`;

const parseDocumentError = async (res) => {
  let message = 'Failed to fetch invoice document';
  try {
    const data = await res.json();
    message = data?.message || message;
  } catch (_) {
    // ignore
  }
  const err = new Error(message);
  err.status = res.status;
  throw err;
};

const getFilenameFromDisposition = (header, fallback) => {
  if (!header) return fallback;
  const match = /filename\*?=(?:UTF-8''|")?([^";\n]+)/i.exec(header);
  if (!match?.[1]) return fallback;
  return decodeURIComponent(match[1].replaceAll('"', ''));
};

const getInvoiceDocumentUrl = (invoiceId, suffix) => `${API_BASE}/api/invoices/${invoiceId}/${suffix}`;

const fetchInvoiceDocumentFile = async (
  invoiceId,
  suffix,
  token,
  { asGuest = false, fallbackFilename } = {}
) => {
  if (asGuest) {
    const res = await guestClient.get(`/api/invoices/${invoiceId}/${suffix}`, { responseType: 'blob' });
    return {
      blob: res.data,
      filename: getFilenameFromDisposition(res.headers?.['content-disposition'], fallbackFilename),
    };
  }
  const res = await fetch(getInvoiceDocumentUrl(invoiceId, suffix), {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    cache: 'no-store',
  });
  if (!res.ok) {
    await parseDocumentError(res);
  }
  return {
    blob: await res.blob(),
    filename: getFilenameFromDisposition(res.headers.get('Content-Disposition'), fallbackFilename),
  };
};

export const fetchInvoicePdfBlob = async (invoiceId, token, { asGuest = false } = {}) => {
  const file = await fetchInvoiceDocumentFile(invoiceId, 'pdf', token, {
    asGuest,
    fallbackFilename: `${invoiceId}.pdf`,
  });
  return file.blob;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const openInvoicePdf = async (
  invoiceId,
  token,
  { asGuest = false, print = false, download = false } = {}
) => {
  if (download) {
    const file = await fetchInvoiceDocumentFile(invoiceId, 'pdf', token, {
      asGuest,
      fallbackFilename: `${invoiceId}.pdf`,
    });
    downloadBlob(file.blob, file.filename || `${invoiceId}.pdf`);
    return null;
  }

  const file = print
    ? await fetchInvoiceDocumentFile(invoiceId, 'print', token, {
        asGuest,
        fallbackFilename: `${invoiceId}.html`,
      })
    : await fetchInvoiceDocumentFile(invoiceId, 'pdf', token, {
        asGuest,
        fallbackFilename: `${invoiceId}.pdf`,
      });

  const url = URL.createObjectURL(file.blob);
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win) {
    URL.revokeObjectURL(url);
    const err = new Error('Popup blocked');
    err.code = 'POPUP_BLOCKED';
    throw err;
  }
  if (print) {
    const triggerPrint = () => window.setTimeout(() => win.print(), 120);
    win.onload = triggerPrint;
    window.setTimeout(triggerPrint, 600);
  }
  return win;
};


